import React from 'react';
import styled from 'styled-components';

/**
 * Unified badge system.
 *
 * Categories:
 *   info      — neutral/gray (No page #, Manual, No OCR)
 *   quality   — amber (Low conf., boundary warnings)
 *   duplicate — red-orange (internal/external duplicates). Pass duplicateScope
 *               to pick the glyph. Both scopes use the two-overlapping-pages
 *               glyph; they are visually distinguished by whether a count is
 *               rendered (internal: count, external: icon-only binary).
 */

export type BadgeType = 'info' | 'quality' | 'duplicate';
export type DuplicateScope = 'internal' | 'external';

const THEMES: Record<BadgeType, { bg: string; color: string; iconColor: string }> = {
    info: { bg: '#f0f0f0', color: '#777', iconColor: '#aaa' },
    quality: { bg: '#fef7ed', color: '#a16207', iconColor: '#ca8a04' },
    duplicate: { bg: '#fef2f2', color: '#b91c1c', iconColor: '#dc2626' },
};

const TWO_OVERLAPPING_PAGES = (color: string) => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <rect x="1" y="2.5" width="5.5" height="6" rx="0.8" stroke={color} strokeWidth="1.1" />
        <rect x="3.5" y="1" width="5.5" height="6" rx="0.8" stroke={color} strokeWidth="1.1" />
    </svg>
);

const DUPLICATE_ICONS: Record<DuplicateScope, (color: string) => React.ReactNode> = {
    internal: TWO_OVERLAPPING_PAGES,
    external: TWO_OVERLAPPING_PAGES,
};

const ICONS: Record<BadgeType, (color: string) => React.ReactNode> = {
    info: (color) => (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <circle cx="5" cy="5" r="4" stroke={color} strokeWidth="1.3" />
            <path d="M5 4.5V7" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="5" cy="3.2" r="0.6" fill={color} />
        </svg>
    ),
    quality: (color) => (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M5 1.5L9 8.5H1L5 1.5Z" stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
            <path d="M5 4.5V6" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
            <circle cx="5" cy="7.2" r="0.5" fill={color} />
        </svg>
    ),
    duplicate: DUPLICATE_ICONS.internal,
};

const BadgeSpan = styled.span<{ $type: BadgeType; $clickable: boolean; $iconOnly: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: ${({ $iconOnly }) => $iconOnly ? '3px' : '1px 6px 1px 4px'};
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 18px;
    white-space: nowrap;
    background: ${({ $type }) => THEMES[$type].bg};
    color: ${({ $type }) => THEMES[$type].color};
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
    transition: filter 0.1s;

    &:hover {
        filter: ${({ $clickable }) => $clickable ? 'brightness(0.95)' : 'none'};
    }
`;

export interface BadgeProps {
    label?: string;
    type?: BadgeType;
    duplicateScope?: DuplicateScope;
    title?: string;
    onClick?: (e: React.MouseEvent) => void;
}

export const Badge: React.FC<BadgeProps> = ({ label, type = 'info', duplicateScope, title, onClick }) => {
    const theme = THEMES[type];
    const icon = type === 'duplicate' && duplicateScope
        ? DUPLICATE_ICONS[duplicateScope]
        : ICONS[type];
    const iconOnly = !label;
    return (
        <BadgeSpan
            $type={type}
            $clickable={!!onClick}
            $iconOnly={iconOnly}
            onClick={onClick}
            title={title}
            aria-label={iconOnly ? title : undefined}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick(e as any);
                }
            } : undefined}
        >
            {icon(theme.iconColor)}
            {label}
        </BadgeSpan>
    );
};
